import {Linking} from 'react-native';
import {AppRoutes} from '../navigation/Routes.types';

export interface AppRoute {
  type?: 'internal' | 'external' | string;
  url?: string;
  params?: Record<string, any> | null;
}

interface Navigator {
  navigate: (name: any, params?: any) => void;
}

interface FilterSetters {
  setSelectedCategory?: (value: any) => void;
  setSelectedBrand?: (value: any) => void;
  setLoadingProduct?: (value: boolean) => void;
}

/**
 * Deep link e lista in-app entregam os params de formas diferentes: a query
 * string vira string ('1', 'true'), o JSON da notificação vira booleano.
 */
const isTruthyParam = (value: any): boolean =>
  value === true || value === 1 || value === '1' || value === 'true';

/**
 * Despacho único de `route -> navegação`, usado pela lista de notificações,
 * pelo AppLinkHandler (deep link do push) e pela modal de comunicação.
 *
 * Antes cada um desses pontos tinha sua própria cópia da regra e todos
 * descartavam os params fora dos casos especiais — por isso abrir o push de
 * "refazer pedido" caía numa sacola vazia.
 */
export function navigateFromRoute(
  route: AppRoute | null | undefined,
  navigator: Navigator,
  filterSetters: FilterSetters = {},
): boolean {
  if (!route?.url) {
    return false;
  }

  if (route.type === 'external' || route.url.startsWith('http')) {
    Linking.openURL(route.url);
    return true;
  }

  const routeKey = Object.keys(AppRoutes).find(
    key => AppRoutes[key as keyof typeof AppRoutes] === route.url,
  );

  if (!routeKey) {
    console.error(`Rota inválida: ${route.url}`);
    return false;
  }

  const params = route.params ?? {};

  if (routeKey === 'MAIN_TAB_SCREEN') {
    if (params.category_name) {
      filterSetters.setSelectedCategory?.(params.category_name);
    }
    if (params.brand_name) {
      filterSetters.setSelectedBrand?.(params.brand_name);
    }
    navigator.navigate(AppRoutes.MAIN_TAB_SCREEN);
    return true;
  }

  if (routeKey === 'PRODUCT_DETAILS' && params.product_sku) {
    filterSetters.setLoadingProduct?.(true);
    navigator.navigate(AppRoutes.PRODUCT_DETAILS, {sku: params.product_sku});
    return true;
  }

  // PED-13 RN-16/RN-23 — a notificação de pedido programado deixou de abrir a
  // SACOLA (editável) e passa a abrir o menu dedicado, de onde o cliente entra
  // na revisão somente leitura. O link antigo, gravado em notificação já
  // entregue, continua chegando com `routeKey: 'BAG'` — por isso a captura
  // acontece ANTES do bloco da sacola, e não no lugar dele.
  if (routeKey === 'SCHEDULED_ORDER_MENU' || isTruthyParam(params.scheduledProposal)) {
    navigator.navigate(AppRoutes.SCHEDULED_ORDER_MENU);
    return true;
  }

  // PAG-01 (26/08/2026) — o push de "você tem um pedido a pagar" passou a
  // chegar com `routeKey: 'ORDERS'` (`backend_clients::avisarPedidoAVistaPendente`).
  // Antes ele vinha como 'scheduled-orders' e caía na captura acima; com o
  // pedido à vista listado em "Meus pedidos", aquele destino levaria o cliente
  // a uma tela onde ele não está mais.
  //
  // Não navega para a revisão direto: com duas lojas do mesmo CPF, abrir um
  // `setId` específico esconderia o outro pedido a pagar. A lista mostra todos.
  if (routeKey === 'ORDERS') {
    navigator.navigate(AppRoutes.ORDERS);
    return true;
  }

  if (routeKey === 'BAG') {
    const bagParams: {
      repeatOrder?: boolean;
      suggestedOrder?: boolean;
    } = {};

    // Precedência do mais específico para o mais genérico.
    if (isTruthyParam(params.suggestedOrder)) {
      bagParams.suggestedOrder = true;
    } else if (isTruthyParam(params.repeatOrder)) {
      bagParams.repeatOrder = true;
    }

    navigator.navigate(AppRoutes.BAG, bagParams);
    return true;
  }

  navigator.navigate(AppRoutes[routeKey as keyof typeof AppRoutes]);
  return true;
}
